import {DocumentPriceBasis} from "@/components/docs/DocumentPriceBasis";
import { useEffect, useRef, useState } from "react";
import {
  Link,
  useBlocker,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Banknote,
  Download,
  FileOutput,
  Pencil,
  Printer,
  Trash2,
} from "lucide-react";
import { canAccess, hasPermission, describe, roleAccess, vnDate } from "@hisense/shared";
import { PageBody } from "@/components/ui/PageHeader";
import { SendDocumentMail } from "@/components/mail/SendDocumentMail";
import { usePageCrumbs } from "@/components/shell/breadcrumb";
import { Panel, PanelHeader } from "@/components/ui/Panel";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Input } from "@/components/ui/Input";
import { ButtonRing } from "@/components/brand/ButtonRing";
import { PaymentStatusChip } from "@/components/domain/StatusChip";
import { PiReceivablePanel } from "@/components/pi/PiReceivablePanel";
import { TableShell, Td, Th, Tr } from "@/components/ui/Table";
import { Modal } from "@/components/ui/Modal";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { ErrorState, LoadingState } from "@/components/ui/States";
import { SaveAsPdfHint } from "@/components/domain/SaveAsPdfHint";
import {
  useAddPayment,
  useCreatePackingFromPi,
  useDeletePayment,
  useDeletePI,
  useProformaInvoice,
  usePiAttachments,
  useSettings,
} from "@/api/hooks";
import { FileDropList, savedToDraft } from "@/components/docs/FileDropList";
import { Select } from "@/components/ui/Select";
import { useAuth } from "@/app/auth";
import { useCan } from "@/app/access";
import { getLocalizedError } from "@/lib/api";
import { parseMoneyMajor } from "@/lib/moneyInput";
import { buildPiHtml, printProformaInvoice } from "@/lib/printPi";
import { DocumentExportDialog } from "@/components/domain/DocumentExportDialog";
import { exportPiCsv } from "@/lib/exportDocument";
import { formatMoney, formatQty, formatUnitPrice } from "@/domain/money";
import { formatDate } from "@/domain/datetime";
import { translateStatus } from "@/domain/i18n";
import { paymentProgress } from "@/domain/status";

function MetaRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-2xs uppercase tracking-wide text-ink-faint">
        {label}
      </dt>
      <dd className="text-sm text-ink">{value}</dd>
    </div>
  );
}

export function ProformaInvoiceDetailPage() {
  const [exportOpen,setExportOpen]=useState(false);
  const [pdfPreview,setPdfPreview]=useState(false);
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { search: context } = useLocation();
  const lock = useRef(false);
  const receiptId = useRef(crypto.randomUUID());
  const [preview, setPreview] = useState(false);
  const [voidId, setVoidId] = useState<string | null>(null);
  const [voidReason, setVoidReason] = useState("");
  const [payConfirm, setPayConfirm] = useState(false);
  const [packingConfirm, setPackingConfirm] = useState(false);
  const { user } = useAuth();
  const can = useCan();
  const { data: pi, isLoading, error, refetch } = useProformaInvoice(id);
  // Bằng chứng đặt hàng của khách (v1.8 mục 4.3) — chỉ đọc ở trang chi tiết; thêm / bỏ ở trang sửa.
  const evidence = usePiAttachments(id);
  const createPacking = useCreatePackingFromPi();
  const addPayment = useAddPayment(id ?? "");
  const deletePayment = useDeletePayment(id ?? "");
  const delPI = useDeletePI();
  const [mailOpen, setMailOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [pay, setPay] = useState({
    date: vnDate(),
    amount: "",
    method: "T/T",
    note: "",
  });
  const settings = useSettings();
  // Legacy feeds this from Settings; the two values production actually uses
  // are the fallback until an administrator configures the list.
  const methodOptions =
    settings.data?.businessOperations.optionLists.paymentMethods ?? [];
  const [actErr, setActErr] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const canUpdate = user ? can("records:update") : false;
  const canCreate = user ? can("records:create") : false;
  // Đơn bán hàng là menu RIÊNG: xem / lập đơn theo ô tick của menu đó, không theo ô của PI (19/09/2026).
  const soAccess = user ? user.access ?? roleAccess(user.role) : null;
  const canViewSo = !!soAccess && canAccess(soAccess, "salesOrders", "view");
  const canCreateSo = !!soAccess && canAccess(soAccess, "salesOrders", "create");
  const canDelete = user ? can("records:delete") : false;
  usePageCrumbs(pi ? [{ label: pi.piNo }] : error ? [{ label: t("common.unavailable") }] : [], `/proforma-invoices${context}`);
  const busy =
    addPayment.isPending ||
    deletePayment.isPending ||
    createPacking.isPending;
  const payDirty = payOpen && Boolean(pay.amount || pay.note);
  const blocker = useBlocker(() => payDirty || lock.current);
  useEffect(() => {
    const guard = (e: BeforeUnloadEvent) => {
      if (payDirty || busy) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", guard);
    return () => window.removeEventListener("beforeunload", guard);
  }, [payDirty, busy]);

  if (isLoading) {
    return (
      <PageBody>
        <LoadingState />
      </PageBody>
    );
  }
  if (error || !pi) {
    return (
      <PageBody>
        <ErrorState message={t("pi.errorLoad")} onRetry={() => refetch()} />
      </PageBody>
    );
  }

  const balance = pi.totalAmount - pi.paid;
  // The stored total already carries upcharge and LESS; show what they applied to.
  const lineSubtotal = pi.items.reduce((n, it) => n + it.amount, 0);
  const progress = paymentProgress(pi.paid, pi.totalAmount);

  const doPrint = () => {setPdfPreview(false);setPreview(true);};

  const doInvoice = () => navigate(`/invoices/new?sourcePi=${pi.id}`);

  const doPay = async () => {
    if (lock.current) return;
    setActErr(null);
    const m = parseMoneyMajor(pay.amount);
    if (m.invalid || m.minor === undefined || m.minor <= 0 || !pay.date) {
      setActErr(t("pi.validAmount"));
      return;
    }
    lock.current = true;
    try {
      await addPayment.mutateAsync({
        requestId: receiptId.current,
        date: new Date(pay.date + "T00:00:00Z").getTime(),
        amount: m.minor,
        method: pay.method || undefined,
        note: pay.note || undefined,
      });
      receiptId.current = crypto.randomUUID();
      setPayConfirm(false);
      setPay({ ...pay, amount: "", note: "" });
      setPayOpen(false);
    } catch (err) {
      setActErr(getLocalizedError(err, t, "pi.errorRecordPayment"));
      setPayConfirm(false);
    } finally {
      lock.current = false;
    }
  };

  const doDelete = async () => {
    try {
      await delPI.mutateAsync(pi.id);
      navigate(`/proforma-invoices${context}`);
    } catch (err) {
      setConfirmDelete(false);
      setActErr(getLocalizedError(err, t, "pi.errorSave"));
    }
  };

  return (
    <PageBody>
      <ConfirmDialog
        open={blocker.state === "blocked"}
        title={t("common.unsavedTitle")}
        description={t("common.unsavedDescription")}
        confirmLabel={t("common.discardChanges")}
        cancelLabel={t("common.keepEditing")}
        busy={busy}
        onCancel={() => blocker.reset?.()}
        onConfirm={() => blocker.proceed?.()}
      />
      {exportOpen && <DocumentExportDialog key={pi.id} documentNo={pi.piNo}
        onCsv={()=>exportPiCsv(pi,t)}
        onPdf={()=>{setExportOpen(false);setPdfPreview(true);setPreview(true);}}
        onExcel={async()=>{const {exportPiExcel}=await import("@/lib/exportPiExcel");return exportPiExcel(pi);}}
        onClose={()=>setExportOpen(false)}/>}
      {preview && (
        <Modal
          className="max-w-6xl"
          title={`${t("piWorkspace.preview")} · ${pi.piNo}`}
          closeLabel={t("common.close")}
          onClose={() => setPreview(false)}
        >
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 p-3">
            <SaveAsPdfHint />
            <Button onClick={() => printProformaInvoice(pi)}>
              {t(pdfPreview ? "docExport.pdfAction" : "common.print")}
            </Button>
          </div>
          <iframe
            title={t("piWorkspace.preview")}
            sandbox="allow-same-origin"
            srcDoc={buildPiHtml(pi)}
            className="min-h-[70dvh] w-full bg-surface"
          />
        </Modal>
      )}
      <ConfirmDialog
        open={payConfirm}
        tone="warn"
        title={t("pi.recordPayment")}
        description={`${pi.piNo} · ${pi.billToName} · ${pay.date} · ${pay.method} · ${formatMoney(parseMoneyMajor(pay.amount).minor ?? 0, pi.currency)}${(parseMoneyMajor(pay.amount).minor ?? 0) > balance ? ` — ${t("piWorkspace.overpayment")}` : ""}`}
        confirmLabel={t("pi.recordPayment")}
        cancelLabel={t("common.cancel")}
        busy={addPayment.isPending}
        onCancel={() => setPayConfirm(false)}
        onConfirm={() => void doPay()}
      />
      {voidId && (
        <Modal
          title={t("piWorkspace.voidPayment")}
          closeLabel={t("common.close")}
          onClose={() => {
            if (!deletePayment.isPending) setVoidId(null);
          }}
        >
          <div className="space-y-3 p-4">
            <p className="text-sm text-ink-soft">{t("piWorkspace.voidHint")}</p>
            <Input
              aria-label={t("piWorkspace.reason")}
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              maxLength={500}
            />
            <Button
              disabled={!voidReason.trim() || deletePayment.isPending}
              onClick={async () => {
                try {
                  await deletePayment.mutateAsync({
                    pid: voidId,
                    reason: voidReason,
                  });
                  setVoidId(null);
                  setVoidReason("");
                } catch (err) {
                  setActErr(getLocalizedError(err, t, "pi.errorSave"));
                }
              }}
            >
              {t("piWorkspace.voidPayment")}
            </Button>
          </div>
        </Modal>
      )}
      {packingConfirm && (
        <Modal
          title={t("pi.createPacking")}
          closeLabel={t("common.close")}
          onClose={() => {
            if (!createPacking.isPending) setPackingConfirm(false);
          }}
        >
          <div className="space-y-3 p-4">
            <p className="text-sm text-ink-soft">
              {t("piWorkspace.packingConfirm", {
                number: pi.piNo,
                count: pi.items.length,
              })}
            </p>
            <TableShell>
              <thead>
                <tr>
                  <Th>{t("pi.item")}</Th>
                  <Th>{t("lot.number")}</Th>
                  <Th>{t("pi.qty")}</Th>
                </tr>
              </thead>
              <tbody>
                {pi.items.map((it) => (
                  <Tr key={it.id}>
                    <Td>
                      <span className="font-mono">
                        {it.productCode} / {it.subRef}
                      </span>
                      <p>{describe(it, pi.printLanguage)}</p>
                    </Td>
                    <Td className="font-mono">{it.lotNumber ?? "—"}</Td>
                    <Td className="font-mono">
                      {it.quantity} {it.unit}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </TableShell>
            <Button
              disabled={createPacking.isPending}
              onClick={async () => {
                if (lock.current) return;
                lock.current = true;
                try {
                  const result = await createPacking.mutateAsync(pi.id);
                  setPackingConfirm(false);
                  lock.current = false;
                  navigate(`/packing-lists/${result.data.id}`);
                } catch (err) {
                  setPackingConfirm(false);
                  setActErr(getLocalizedError(err, t, "pi.errorSave"));
                } finally {
                  lock.current = false;
                }
              }}
            >
              {t("pi.createPacking")}
            </Button>
          </div>
        </Modal>
      )}
      <ConfirmDialog
        open={confirmDelete}
        title={pi.piNo}
        description={t("pi.confirmDelete", { number: pi.piNo })}
        confirmLabel={t("common.delete")}
        cancelLabel={t("common.cancel")}
        busy={delPI.isPending}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => void doDelete()}
      />
      <DocumentPriceBasis snapshots={pi.priceSnapshots}/>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <ButtonRing
            size={46}
            holes={2}
            tone={
              pi.paymentStatus === "paid"
                ? "ok"
                : pi.paymentStatus === "partial"
                  ? "warn"
                  : "neutral"
            }
            progress={progress}
            title={t("status.paymentProgress", {
              status: translateStatus(t, pi.paymentStatus),
              percent: Math.round(progress * 100),
            })}
          />
          <h1 className="font-mono tabular text-xl font-semibold">{pi.piNo}</h1>
          {pi.pProcess && <Badge>{t("lot.pProcess")}</Badge>}
          {pi.hasInvoice && <Badge tone="brand">{t("pi.invoiced")}</Badge>}
          {pi.adminOnly && <Badge tone="warn">{t("pi.adminOnly")}</Badge>}
          {pi.salesOrder && (canViewSo
            ? <Link className="text-brand underline" to={`/sales-orders/${pi.salesOrder.id}`}>
                <Badge tone="brand">{t("so.committed")} · {pi.salesOrder.soNo} · {t(`so.${pi.salesOrder.status}`)}</Badge>
              </Link>
            : <Badge tone="brand">{t("so.committed")} · {pi.salesOrder.soNo} · {t(`so.${pi.salesOrder.status}`)}</Badge>)}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {pi.salesOrder
            ? canViewSo && <Button variant="secondary" size="sm" onClick={()=>navigate(`/sales-orders/${pi.salesOrder!.id}`)}>{t('so.openOrder')}</Button>
            : canCreateSo && <Button variant="secondary" size="sm" onClick={()=>navigate(`/sales-orders/new?piId=${pi.id}`)}>{t('so.fromPi')}</Button>}
          <Button variant="secondary" size="sm" onClick={() => setMailOpen(true)}>
            {t("mail.title")}
          </Button>
          <Button variant="secondary" size="sm" onClick={doPrint}>
            <Printer className="size-4" />
            {t("common.print")}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setExportOpen(true)}
            aria-haspopup="dialog"
          >
            <Download className="size-4" />
            {t("common.export")}
          </Button>
          {canUpdate && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                navigate(`/proforma-invoices/${pi.id}/edit${context}`)
              }
            >
              <Pencil className="size-4" />
              {t("common.edit")}
            </Button>
          )}
          {canCreate && !pi.hasInvoice && (
            <Button
              variant="secondary"
              size="sm"
              onClick={doInvoice}
              disabled={busy}
            >
              <FileOutput className="size-4" />
              {t("pi.convertInvoice")}
            </Button>
          )}
          {canCreate && (
            <Button
              variant="secondary"
              size="sm"
              disabled={createPacking.isPending}
              onClick={() => setPackingConfirm(true)}
            >
              {t("pi.createPacking")}
            </Button>
          )}
          {canCreate && hasPermission(user!.role, "supplier_cost:view") && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                navigate(
                  `/purchase-orders/new?mode=pi&sourcePi=${pi.id}&sourcePiNo=${encodeURIComponent(pi.piNo ?? "")}`,
                )
              }
            >
              {t("pi.createPo")}
            </Button>
          )}
          {canDelete && (
            <Button
              variant="danger"
              size="sm"
              aria-label={t("common.delete")}
              onClick={() => setConfirmDelete(true)}
            >
              <Trash2 className="size-4" />
            </Button>
          )}
          {canCreate && (
            <Button size="sm" onClick={() => setPayOpen(true)}>
              <Banknote className="size-4" />
              {t("pi.recordPayment")}
            </Button>
          )}
        </div>
      </div>

      {mailOpen && (
        <SendDocumentMail
          onClose={() => setMailOpen(false)}
          doc={{
            kind: "pi",
            documentNo: pi.piNo,
            customerName: pi.billToName ?? null,
            date: pi.date ? vnDate(pi.date) : null,
            customerPoNo: pi.customerPoNo,
            productCodes: [...new Set(pi.items.map((i) => i.productCode).filter(Boolean) as string[])],
          }}
        />
      )}

      {/* Nói TRƯỚC khi người dùng bấm Sửa, không phải sau khi máy chủ từ chối. */}
      {pi.salesOrder && ["Confirmed", "Closed"].includes(pi.salesOrder.status) && (
        <p className="mt-2 rounded-sm border border-warn/30 bg-warn-tint px-3 py-2 text-xs">
          {t("so.piLocked")}
        </p>
      )}
      {actErr && (
        <p className="mt-2 rounded-sm bg-danger-tint px-3 py-2 text-xs text-danger">
          {actErr}
        </p>
      )}
      {payOpen && (
        <Panel className="mt-3 p-3">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const amount = parseMoneyMajor(pay.amount);
              if (
                amount.invalid ||
                !amount.minor ||
                amount.minor <= 0 ||
                !pay.date
              ) {
                setActErr(t("pi.validAmount"));
                return;
              }
              setPayConfirm(true);
            }}
            className="flex flex-wrap items-end gap-2"
          >
            <div>
              <span className="mb-1 block text-xs text-ink-soft">
                {t("common.date")}
              </span>
              <Input
                aria-label={t("common.date")}
                type="date"
                required
                value={pay.date}
                onChange={(e) => setPay({ ...pay, date: e.target.value })}
                className="w-40"
              />
            </div>
            <div>
              <span className="mb-1 block text-xs text-ink-soft">
                {t("pi.amount")} ({pi.currency})
              </span>
              <Input
                aria-label={t("pi.amount")}
                inputMode="decimal"
                value={pay.amount}
                onChange={(e) => setPay({ ...pay, amount: e.target.value })}
                placeholder="0.00"
                className="w-32"
              />
            </div>
            <div>
              <span className="mb-1 block text-xs text-ink-soft">
                {t("common.method")}
              </span>
              {methodOptions.length ? (
                <Select
                  containerClassName="w-32"
                  value={pay.method}
                  onChange={(e) => setPay({ ...pay, method: e.target.value })}
                >
                  {!methodOptions.includes(pay.method) && pay.method && (
                    <option value={pay.method}>{pay.method}</option>
                  )}
                  {methodOptions.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </Select>
              ) : (
                <Input
                  value={pay.method}
                  onChange={(e) => setPay({ ...pay, method: e.target.value })}
                  className="w-28"
                />
              )}
            </div>
            <div className="min-w-40 flex-1">
              <span className="mb-1 block text-xs text-ink-soft">
                {t("pi.paymentNote")}
              </span>
              <Input
                aria-label={t("pi.paymentNote")}
                value={pay.note}
                onChange={(e) => setPay({ ...pay, note: e.target.value })}
              />
            </div>
            <Button type="submit" size="md" disabled={addPayment.isPending}>
              {addPayment.isPending ? "…" : t("pi.recordPayment")}
            </Button>
          </form>
        </Panel>
      )}

      <div className="mt-4 grid grid-cols-1 gap-3 xl:grid-cols-[minmax(0,1fr)_300px]">
        {/* Left: document body */}
        <div className="flex min-w-0 flex-col gap-3">
          <Panel className="p-4">
            <dl className="grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3">
              <MetaRow
                label={t("pi.billTo")}
                value={<span className="font-medium">{pi.billToName}</span>}
              />
              <MetaRow
                label={t("pi.poNo")}
                value={
                  <span className="font-mono tabular">{pi.customerPoNo}</span>
                }
              />
              <MetaRow
                label={t("pi.date")}
                value={
                  <span className="font-mono tabular">
                    {formatDate(pi.date)}
                  </span>
                }
              />
              <MetaRow
                label={t("common.delivery")}
                value={
                  <span className="font-mono tabular">
                    {formatDate(pi.deliveryDate)}
                  </span>
                }
              />
              <MetaRow label={t("common.shipping")} value={pi.shippingMode} />
              <MetaRow label={t("pi.respBy")} value={pi.respBy} />
              {/* Khối liên hệ ĐÃ CHỤP lúc lập chứng từ là nhiều dòng (tên / địa chỉ /
                  điện thoại / email / WeChat). Không giữ xuống dòng thì tất cả dồn
                  thành một dòng dài và người đọc không tách ra được. */}
              {pi.billToContact && (
                <MetaRow
                  label={t("pi.billToContactPerson")}
                  value={
                    <span className="whitespace-pre-line">
                      {pi.billToContact}
                    </span>
                  }
                />
              )}
              {pi.customerDept && (
                <MetaRow label={t("pi.customerDept")} value={pi.customerDept} />
              )}
              {pi.paymentCompany && (
                <MetaRow
                  label={t("pi.paymentCompany")}
                  value={pi.paymentCompany}
                />
              )}
              {pi.notifyPartyName && (
                <MetaRow
                  label={t("pi.notifyPartyCustomer")}
                  value={
                    <span className="whitespace-pre-line">
                      {pi.notifyPartyName}
                      {pi.notifyPartyContact
                        ? `\n${pi.notifyPartyContact}`
                        : ""}
                    </span>
                  }
                />
              )}
              {pi.consigneeName && (
                <MetaRow
                  label={t("pi.consigneeCustomer")}
                  value={
                    <span className="whitespace-pre-line">
                      {pi.consigneeName}
                      {pi.consigneeContact ? `\n${pi.consigneeContact}` : ""}
                    </span>
                  }
                />
              )}
              {pi.countryOfOrigin && (
                <MetaRow
                  label={t("pi.countryOfOrigin")}
                  value={pi.countryOfOrigin}
                />
              )}
              {pi.portOfLoading && (
                <MetaRow
                  label={t("pi.portOfLoading")}
                  value={pi.portOfLoading}
                />
              )}
              {pi.shippingMarks && (
                <MetaRow
                  label={t("pi.shippingMarks")}
                  value={pi.shippingMarks}
                />
              )}
              {pi.dnNo && (
                <MetaRow
                  label={t("pi.dnNo")}
                  value={<span className="font-mono tabular">{pi.dnNo}</span>}
                />
              )}
            </dl>
            {pi.remarks && (
              <div className="mt-4 border-t border-line pt-3">
                <div className="text-2xs uppercase tracking-wide text-ink-faint">
                  {t("common.remarks")}
                </div>
                <p className="mt-1 whitespace-pre-line text-sm text-ink-soft">
                  {pi.remarks}
                </p>
              </div>
            )}
          </Panel>

          <Panel className="p-4">
            <h2 className="text-sm font-semibold text-ink">{t("evidence.piTitle")}</h2>
            <p className="mb-3 mt-0.5 text-xs text-ink-soft">{t("evidence.piHint")}</p>
            {/* Đang tải / tải lỗi thì nói đúng như vậy — không hiện "Chưa có tệp" khi thật ra có. */}
            {evidence.isLoading ? (
              <LoadingState />
            ) : evidence.isError ? (
              <ErrorState message={t("evidence.listLoadFailed")} onRetry={() => void evidence.refetch()} />
            ) : (
              <FileDropList files={(evidence.data ?? []).map(savedToDraft)} folder="pi-attachments" readOnly />
            )}
          </Panel>

          <Panel>
            <PanelHeader
              eyebrow={t("pi.lineItems")}
              title={
                <span className="font-mono tabular text-sm">
                  {t("pi.linesCount", { count: pi.items.length })} ·{" "}
                  {pi.paymentTerms}
                </span>
              }
            />
            <TableShell className="rounded-none border-0">
              <thead>
                <tr>
                  <Th className="w-8" align="right">
                    #
                  </Th>
                  <Th>{t("pi.item")}</Th>
                  <Th>{t("common.colorSize")}</Th>
                  <Th align="right">{t("pi.qty")}</Th>
                  <Th align="right">{t("pi.unitPrice")}</Th>
                  <Th align="right">{t("pi.amount")}</Th>
                </tr>
              </thead>
              <tbody>
                {pi.items.map((it) => (
                  <Tr key={it.lineNo}>
                    <Td
                      align="right"
                      className="font-mono tabular text-xs text-ink-faint"
                    >
                      {String(it.lineNo).padStart(2, "0")}
                    </Td>
                    <Td>
                      <div className="font-mono tabular text-xs font-medium text-ink">
                        {it.productCode}
                        {it.subRef && (
                          <span className="ml-1 text-ink-faint">
                            / {it.subRef}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-ink-soft">
                        {describe(it, pi.printLanguage)}
                      </div>
                      {it.lotNumber && (
                        <div className="font-mono text-xs">
                          {t("lot.number")}: {it.lotNumber}
                        </div>
                      )}
                      {it.customerStyleNo && (
                        <div className="text-2xs text-ink-faint">
                          {t("common.customerStyleNo")}: {it.customerStyleNo}
                        </div>
                      )}
                    </Td>
                    <Td className="text-xs text-ink-soft">
                      {it.color} · {it.size}
                    </Td>
                    <Td align="right" className="font-mono tabular text-ink">
                      {formatQty(it.quantity)}
                      <span className="ml-1 text-2xs text-ink-faint">
                        {it.unit}
                      </span>
                    </Td>
                    <Td
                      align="right"
                      className="font-mono tabular text-ink-soft"
                    >
                      {formatUnitPrice(it.unitPrice, pi.currency)}
                      {it.exwPrice != null && (
                        <div className="text-2xs text-ink-faint">
                          {t("common.exwPrice")}:{" "}
                          {formatUnitPrice(it.exwPrice, pi.currency)}
                        </div>
                      )}
                    </Td>
                    <Td
                      align="right"
                      className="font-mono tabular font-medium text-ink"
                    >
                      {formatUnitPrice(it.amount, pi.currency)}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </TableShell>
            {/* Totals */}
            <div className="flex justify-end border-t border-line px-4 py-3">
              <dl className="w-64 space-y-1.5 text-sm">
                <div className="flex justify-between text-ink-soft">
                  <dt>{t("common.subtotal")}</dt>
                  <dd className="font-mono tabular">
                    {formatUnitPrice(lineSubtotal, pi.currency)}
                  </dd>
                </div>
                {pi.upcharge !== 0 && (
                  <div className="flex justify-between text-ink-soft">
                    <dt>{t("pi.upcharge")}</dt>
                    <dd className="font-mono tabular">
                      {formatUnitPrice(pi.upcharge, pi.currency)}
                    </dd>
                  </div>
                )}
                {pi.discount !== 0 && (
                  <div className="flex justify-between text-ink-soft">
                    <dt>{t("pi.discountPct")}</dt>
                    <dd className="font-mono tabular">{pi.discount / 100}%</dd>
                  </div>
                )}
                <div className="flex justify-between border-t border-line pt-1.5 text-base font-semibold text-ink">
                  <dt>
                    {t("pi.total")} · {pi.currency}
                  </dt>
                  <dd className="font-mono tabular">
                    {formatMoney(pi.totalAmount, pi.currency)}
                  </dd>
                </div>
              </dl>
            </div>
          </Panel>
        </div>

        {/* Right: payment sub-ledger */}
        <div className="flex min-w-0 flex-col gap-3">
          <Panel className="overflow-hidden">
            <div className="flex flex-col items-center gap-2 border-b border-line bg-sunken/40 px-4 py-5">
              <ButtonRing
                size={88}
                holes={2}
                tone={
                  pi.paymentStatus === "paid"
                    ? "ok"
                    : pi.paymentStatus === "partial"
                      ? "warn"
                      : "neutral"
                }
                progress={progress}
              />
              <div className="mt-1 text-center">
                <div className="font-mono tabular text-2xl font-semibold text-ink">
                  {Math.round(progress * 100)}%
                </div>
                <PaymentStatusChip
                  status={pi.paymentStatus}
                  paid={pi.paid}
                  total={pi.totalAmount}
                  showRing={false}
                />
              </div>
            </div>
            <dl className="divide-y divide-line text-sm">
              <div className="flex justify-between px-4 py-2.5">
                <dt className="text-ink-soft">{t("pi.total")}</dt>
                <dd className="font-mono tabular font-medium text-ink">
                  {formatMoney(pi.totalAmount, pi.currency)}
                </dd>
              </div>
              <div className="flex justify-between px-4 py-2.5">
                <dt className="text-ink-soft">{t("pi.paid")}</dt>
                <dd className="font-mono tabular text-ok">
                  {formatMoney(pi.paid, pi.currency)}
                </dd>
              </div>
              <div className="flex justify-between bg-sunken/30 px-4 py-2.5">
                <dt className="font-medium text-ink">
                  {t(balance < 0 ? "piWorkspace.credit" : "pi.balance")}
                </dt>
                <dd className="font-mono tabular font-semibold text-ink">
                  {formatMoney(Math.abs(balance), pi.currency)}
                </dd>
              </div>
            </dl>
          </Panel>

          <PiReceivablePanel piId={pi.id} currency={pi.currency} />

          <Panel>
            <PanelHeader title={t("pi.payments")} />
            {pi.payments.length === 0 ? (
              <p className="px-4 py-6 text-center text-xs text-ink-faint">
                {t("pi.noPayments")}
              </p>
            ) : (
              <ul className="divide-y divide-line">
                {pi.payments.map((pay) => (
                  <li
                    key={pay.id}
                    className="flex items-center justify-between gap-2 px-4 py-2.5 text-sm"
                  >
                    <div>
                      <div className="font-mono tabular text-xs text-ink">
                        {formatDate(pay.date)}
                      </div>
                      <div className="text-2xs uppercase tracking-wide text-ink-faint">
                        {pay.method}
                      </div>
                      {pay.createdByName && (
                        <div className="text-2xs text-ink-faint">
                          {pay.createdByName}
                        </div>
                      )}
                      {pay.voidedAt && (
                        <div className="text-2xs text-danger">
                          {t("piWorkspace.voided")} · {formatDate(pay.voidedAt)}{" "}
                          · {pay.voidedByName ?? "—"} · {pay.voidReason}
                        </div>
                      )}
                      {pay.note && (
                        <div className="text-2xs text-ink-soft">{pay.note}</div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono tabular font-medium text-ok">
                        {formatMoney(pay.amount, pi.currency)}
                      </span>
                      {canDelete && !pay.voidedAt && (
                        <button
                          type="button"
                          aria-label={t("piWorkspace.voidPayment")}
                          onClick={() => {
                            setVoidId(pay.id);
                            setVoidReason("");
                          }}
                          className="text-ink-faint hover:text-danger"
                        >
                          <Trash2 className="size-3.5" />
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>
      </div>
    </PageBody>
  );
}
